import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "react-oidc-context";
import { setOidcAuthState } from "../store/authUserSlice";

// Mirrors the OIDC library's auth state (Scopes + isAuthenticated) into
// Redux, so the plain (state) => boolean authorization selectors in
// authUserSlice.js can stay the same shape while reading Scope data under
// the OIDC path (see docs/specs/0001-oidc-migration.md, ticket 07). Must be
// rendered inside AuthProvider (uses useAuth()).
function OidcReduxScopeBridge({ children }) {
  const auth = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    const profile = auth.user?.profile;
    // The backend's per-user authorization Scopes (org:admin, ensaras:admin,
    // portal:internal, ...) ride on a custom `scope` claim inside the
    // profile/userinfo response (see portal's
    // user/oidc_provider_settings.py, PortalScopeClaims) - NOT on
    // `auth.user.scopes`, which oidc-client-ts derives from the top-level
    // OAuth `scope` field of the token response. That field is just the
    // generic requested/granted OAuth scope string (e.g. "openid profile
    // email offline_access"), identical for every user regardless of role
    // (ticket 12 - ticket 07 wired this to the wrong source).
    const scopes = profile?.scope ? profile.scope.split(" ") : [];
    dispatch(
      setOidcAuthState({
        isAuthenticated: auth.isAuthenticated,
        scopes,
        // Claims on the ID token (see docs/specs/0001-oidc-migration.md) -
        // which of these the backend's Authorization Server actually
        // includes is unconfirmed; components degrade to a blank
        // username/email if the claim is absent, rather than crashing.
        userId: profile?.sub ?? null,
        username: profile?.preferred_username ?? profile?.name ?? profile?.email ?? null,
        email: profile?.email ?? null,
        permissions: {
          canAdd: profile?.can_add ?? false,
          canEdit: profile?.can_edit ?? false,
          canDelete: profile?.can_delete ?? false,
          canDownload: profile?.can_download ?? false,
        }
      })
    );
  }, [dispatch, auth.isAuthenticated, auth.user]);

  return children;
}

export default OidcReduxScopeBridge;
